import * as React from 'react';
import {
  statusChipSeparatorRecipe,
  statusChipTimeRecipe,
} from '../recipes/mobile.recipe';
import { Badge } from './Badge';
import { ElapsedTime } from './ElapsedTime';
import type { StatusTone } from './status-tone';

export interface StatusChipProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** The status in words: "Dispatching", "Towing". */
  label: string;
  /** Its colour, usually the job phase. Neutral when left off. */
  tone?: StatusTone;
  /** When the status began; the chip counts up from it. */
  since?: Date | string | number;
  /**
   * `md` sits in a list row, a header or the active-call pill; `lg` is a
   * status offered as a choice, in the status sheet.
   */
  size?: 'md' | 'lg';
}

/**
 * A call's status on a phone, as the Figma's Status chip draws it: the word
 * in its tone on a tint of it, and how long the call has been in it.
 *
 *   <StatusChip label="Dispatching" tone="phase-dispatched" since={call.startedAt} />
 *
 * One chip for the list row, the call's header and the active-call pill, so
 * the three cannot drift apart. A Badge underneath, at the phone's sizes.
 * It is every status pill on the phone, a task's and an inspection's too;
 * the smaller Badge is the dashboard's (README.md, "A status pill").
 */
export const StatusChip = React.forwardRef<HTMLSpanElement, StatusChipProps>(
  function StatusChip(
    { label, tone = 'neutral', since, size = 'md', ...props },
    ref
  ) {
    return (
      <Badge
        ref={ref}
        tone={tone}
        size={size === 'lg' ? '2xl' : 'xl'}
        {...props}
      >
        {label}
        {since !== undefined ? (
          <>
            <span className={statusChipSeparatorRecipe()} aria-hidden="true">
              •
            </span>
            <ElapsedTime since={since} className={statusChipTimeRecipe()} />
          </>
        ) : null}
      </Badge>
    );
  }
);
